import React, { useState, useEffect } from 'react';
import { jobsAPI } from '../../utils/api';
import './JobEditor.css';

/**
 * JobEditor Component
 *
 * Full-featured job posting editor modal
 * Allows editing all job fields including:
 * - Basic info (title, company, location, etc.)
 * - Description
 * - Responsibilities, Requirements, Nice-to-have
 * - Salary, Benefits
 * - Status and visibility
 */
const JobEditor = ({ jobId, onClose, onSave }) => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const [formData, setFormData] = useState({
    title: '',
    slug: '',
    company: '',
    department: '',
    location: '',
    workMode: 'remote',
    employmentType: 'full-time',
    experienceLevel: 'mid',
    description: '',
    responsibilities: [],
    requirements: [],
    niceToHave: [],
    salaryRange: {
      min: '',
      max: '',
      currency: 'USD'
    },
    benefits: [],
    status: 'draft',
    isPublic: false,
    applicationDeadline: '',
    expiresAt: '',
    autoCloseOnExpiry: true
  });

  // Track which array fields are in "bulk edit" mode
  const [bulkMode, setBulkMode] = useState({});
  const [bulkText, setBulkText] = useState({});

  // Helper to format date for input[type="date"]
  const formatDateForInput = (dateStr) => {
    if (!dateStr) return '';
    const d = new Date(dateStr);
    if (isNaN(d.getTime())) return '';
    return d.toISOString().split('T')[0];
  };

  useEffect(() => {
    if (jobId) {
      loadJob();
    } else {
      setLoading(false);
    }
  }, [jobId]);

  const loadJob = async () => {
    try {
      setLoading(true);
      const response = await jobsAPI.getById(jobId);
      const job = response.data.job;

      setFormData({
        title: job.title || '',
        slug: job.slug || '',
        company: job.company || '',
        department: job.department || '',
        location: job.location || '',
        workMode: job.workMode || 'remote',
        employmentType: job.employmentType || 'full-time',
        experienceLevel: job.experienceLevel || 'mid',
        description: job.description || '',
        responsibilities: job.responsibilities || [],
        requirements: job.requirements || [],
        niceToHave: job.niceToHave || [],
        salaryRange: {
          min: job.salaryRange?.min || '',
          max: job.salaryRange?.max || '',
          currency: job.salaryRange?.currency || 'USD'
        },
        benefits: job.benefits || [],
        status: job.status || 'draft',
        isPublic: job.isPublic || false,
        applicationDeadline: formatDateForInput(job.applicationDeadline),
        expiresAt: formatDateForInput(job.expiresAt),
        autoCloseOnExpiry: job.autoCloseOnExpiry !== undefined ? job.autoCloseOnExpiry : true
      });
    } catch (err) {
      console.error('[JobEditor] Error loading job:', err);
      setError(err.response?.data?.error || 'Failed to load job');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      setSaving(true);
      setError('');

      const toEndOfDay = (val) => {
        if (!val) return val;
        if (/^\d{4}-\d{2}-\d{2}$/.test(val)) {
          return new Date(val + 'T23:59:59').toISOString();
        }
        return val;
      };

      const payload = {
        ...formData,
        applicationDeadline: toEndOfDay(formData.applicationDeadline),
        expiresAt: toEndOfDay(formData.expiresAt),
      };

      if (jobId) {
        await jobsAPI.update(jobId, payload);
      } else {
        await jobsAPI.create(payload);
      }

      onSave();
      onClose();
    } catch (err) {
      console.error('[JobEditor] Error saving job:', err);
      setError(err.response?.data?.error || 'Failed to save job');
      setSaving(false);
    }
  };

  const handleRemoveItem = (type, index) => {
    setFormData({
      ...formData,
      [type]: formData[type].filter((_, i) => i !== index)
    });
  };

  const handleEditItem = (type, index, newValue) => {
    const updated = [...formData[type]];
    updated[index] = newValue;
    setFormData({ ...formData, [type]: updated });
  };

  const handleEnterBulkMode = (type) => {
    const text = formData[type].join('\n');
    setBulkText({ ...bulkText, [type]: text });
    setBulkMode({ ...bulkMode, [type]: true });
  };

  const handleSaveBulk = (type) => {
    const text = bulkText[type] || '';
    const items = text
      .split('\n')
      .map(line => line.replace(/^[\s•\-\d.]+/, '').trim())
      .filter(line => line.length > 0);
    setFormData({ ...formData, [type]: items });
    setBulkMode({ ...bulkMode, [type]: false });
    setBulkText({ ...bulkText, [type]: '' });
  };

  const handleCancelBulk = (type) => {
    setBulkMode({ ...bulkMode, [type]: false });
    setBulkText({ ...bulkText, [type]: '' });
  };

  const renderArrayField = (type, label) => {
    const isBulk = bulkMode[type];

    return (
      <div className="form-group">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem' }}>
          <label className="form-label" style={{ margin: 0 }}>{label}</label>
          {!isBulk && (
            <button
              type="button"
              onClick={() => handleEnterBulkMode(type)}
              style={{
                background: 'none',
                border: '1px solid #D1D5DB',
                color: '#6B7280',
                padding: '4px 10px',
                borderRadius: '4px',
                fontSize: '0.78rem',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '4px',
                transition: 'all 0.15s'
              }}
              onMouseEnter={(e) => { e.target.style.background = '#F3F4F6'; e.target.style.color = '#374151'; }}
              onMouseLeave={(e) => { e.target.style.background = 'none'; e.target.style.color = '#6B7280'; }}
              title="Switch to bulk edit mode - add/edit multiple items at once"
            >
              Bulk Edit
            </button>
          )}
        </div>

        {isBulk ? (
          <div className="bulk-edit-container">
            <textarea
              className="form-textarea bulk-textarea"
              rows="6"
              value={bulkText[type] || ''}
              onChange={(e) => setBulkText({ ...bulkText, [type]: e.target.value })}
              placeholder={`Enter one ${label.toLowerCase().replace(/s$/, '')} per line.\n\nYou can paste from any document - bullet points and numbering will be auto-stripped.\n\nExample:\n- First item\n- Second item\n- Third item`}
              autoFocus
            />
            <div style={{ display: 'flex', gap: '0.5rem', marginTop: '0.5rem' }}>
              <button
                type="button"
                className="btn-add"
                onClick={() => handleSaveBulk(type)}
              >
                Save {(bulkText[type] || '').split('\n').filter(l => l.trim()).length} Items
              </button>
              <button
                type="button"
                onClick={() => handleCancelBulk(type)}
                style={{
                  padding: '0.4rem 0.8rem',
                  background: 'white',
                  border: '1px solid #D1D5DB',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  fontSize: '0.85rem',
                  color: '#6B7280'
                }}
              >
                Cancel
              </button>
            </div>
            <small style={{ color: '#9CA3AF', fontSize: '0.75rem', marginTop: '0.25rem', display: 'block' }}>
              One item per line. Bullets, dashes, and numbers are auto-removed.
            </small>
          </div>
        ) : (
          <>
            {formData[type].length > 0 && (
              <div className="array-items">
                {formData[type].map((item, index) => (
                  <div key={index} className="array-item editable-item">
                    <span
                      style={{ color: '#9CA3AF', fontSize: '0.8rem', minWidth: '20px', userSelect: 'none' }}
                    >
                      {index + 1}.
                    </span>
                    <input
                      type="text"
                      className="array-item-edit-input"
                      value={item}
                      onChange={(e) => handleEditItem(type, index, e.target.value)}
                      style={{
                        flex: 1,
                        border: 'none',
                        background: 'transparent',
                        fontSize: '0.9rem',
                        padding: '2px 4px',
                        outline: 'none',
                        color: '#374151'
                      }}
                    />
                    <button
                      type="button"
                      className="btn-remove"
                      onClick={() => handleRemoveItem(type, index)}
                      title="Remove item"
                    >
                      ×
                    </button>
                  </div>
                ))}
              </div>
            )}
            <button
              type="button"
              onClick={() => {
                setFormData({ ...formData, [type]: [...formData[type], ''] });
                setTimeout(() => {
                  const inputs = document.querySelectorAll(`.array-item-edit-input`);
                  const lastInput = inputs[inputs.length - 1];
                  if (lastInput) lastInput.focus();
                }, 50);
              }}
              style={{
                marginTop: '0.5rem',
                background: 'none',
                border: '1px dashed #D1D5DB',
                color: '#6B7280',
                padding: '0.5rem',
                borderRadius: '6px',
                cursor: 'pointer',
                width: '100%',
                fontSize: '0.85rem',
                transition: 'all 0.15s'
              }}
              onMouseEnter={(e) => { e.target.style.borderColor = '#4F46E5'; e.target.style.color = '#4F46E5'; e.target.style.background = '#EEF2FF'; }}
              onMouseLeave={(e) => { e.target.style.borderColor = '#D1D5DB'; e.target.style.color = '#6B7280'; e.target.style.background = 'none'; }}
            >
              + Add Item
            </button>
          </>
        )}
      </div>
    );
  };

  if (loading) {
    return (
      <div className="job-editor-modal">
        <div className="job-editor-content">
          <div className="loading-state">
            <div className="spinner"></div>
            <p>Loading job...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="job-editor-modal" onClick={onClose}>
      <div className="job-editor-content" onClick={(e) => e.stopPropagation()}>
        <div className="job-editor-header">
          <h2>{jobId ? 'Edit Job Posting' : 'Create Job Posting'}</h2>
          <button className="btn-close" onClick={onClose}>×</button>
        </div>

        {error && (
          <div className="error-banner">
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit} className="job-editor-form">
          {/* Basic Information */}
          <section className="form-section">
            <h3>Basic Information</h3>

            <div className="form-row">
              <div className="form-group">
                <label className="form-label">Job Title *</label>
                <input
                  type="text"
                  className="form-input"
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  required
                />
              </div>

              <div className="form-group">
                <label className="form-label">URL Slug *</label>
                <input
                  type="text"
                  className="form-input slug-input"
                  value={formData.slug}
                  onChange={(e) => setFormData({ ...formData, slug: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '-') })}
                  required
                />
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label className="form-label">Company *</label>
                <input
                  type="text"
                  className="form-input"
                  value={formData.company}
                  onChange={(e) => setFormData({ ...formData, company: e.target.value })}
                  required
                />
              </div>

              <div className="form-group">
                <label className="form-label">Department</label>
                <input
                  type="text"
                  className="form-input"
                  value={formData.department}
                  onChange={(e) => setFormData({ ...formData, department: e.target.value })}
                />
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label className="form-label">Location *</label>
                <input
                  type="text"
                  className="form-input"
                  value={formData.location}
                  onChange={(e) => setFormData({ ...formData, location: e.target.value })}
                  required
                />
              </div>

              <div className="form-group">
                <label className="form-label">Work Mode</label>
                <select
                  className="form-select"
                  value={formData.workMode}
                  onChange={(e) => setFormData({ ...formData, workMode: e.target.value })}
                >
                  <option value="remote">Remote</option>
                  <option value="hybrid">Hybrid</option>
                  <option value="onsite">Onsite</option>
                </select>
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label className="form-label">Employment Type</label>
                <select
                  className="form-select"
                  value={formData.employmentType}
                  onChange={(e) => setFormData({ ...formData, employmentType: e.target.value })}
                >
                  <option value="full-time">Full-time</option>
                  <option value="part-time">Part-time</option>
                  <option value="contract">Contract</option>
                  <option value="internship">Internship</option>
                </select>
              </div>

              <div className="form-group">
                <label className="form-label">Experience Level</label>
                <select
                  className="form-select"
                  value={formData.experienceLevel}
                  onChange={(e) => setFormData({ ...formData, experienceLevel: e.target.value })}
                >
                  <option value="entry">Entry Level</option>
                  <option value="mid">Mid Level</option>
                  <option value="senior">Senior</option>
                  <option value="lead">Lead</option>
                  <option value="executive">Executive</option>
                </select>
              </div>
            </div>
          </section>

          {/* Description */}
          <section className="form-section">
            <h3>Job Description</h3>
            <div className="form-group">
              <label className="form-label">Description *</label>
              <textarea
                className="form-textarea"
                rows="8"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                required
                placeholder="Describe the role, team, and what makes this opportunity great..."
              />
            </div>
          </section>

          {/* Responsibilities */}
          <section className="form-section">
            <h3>Responsibilities</h3>
            {renderArrayField('responsibilities', 'Responsibilities')}
          </section>

          {/* Requirements */}
          <section className="form-section">
            <h3>Requirements</h3>
            {renderArrayField('requirements', 'Requirements')}
          </section>

          {/* Nice to Have */}
          <section className="form-section">
            <h3>Nice to Have</h3>
            {renderArrayField('niceToHave', 'Nice to Have')}
          </section>

          {/* Compensation */}
          <section className="form-section">
            <h3>Compensation</h3>
            <div className="form-row">
              <div className="form-group">
                <label className="form-label">Min Salary</label>
                <input
                  type="number"
                  className="form-input"
                  value={formData.salaryRange.min}
                  onChange={(e) => setFormData({
                    ...formData,
                    salaryRange: { ...formData.salaryRange, min: e.target.value }
                  })}
                  placeholder="50000"
                />
              </div>

              <div className="form-group">
                <label className="form-label">Max Salary</label>
                <input
                  type="number"
                  className="form-input"
                  value={formData.salaryRange.max}
                  onChange={(e) => setFormData({
                    ...formData,
                    salaryRange: { ...formData.salaryRange, max: e.target.value }
                  })}
                  placeholder="100000"
                />
              </div>

              <div className="form-group">
                <label className="form-label">Currency</label>
                <select
                  className="form-select"
                  value={formData.salaryRange.currency}
                  onChange={(e) => setFormData({
                    ...formData,
                    salaryRange: { ...formData.salaryRange, currency: e.target.value }
                  })}
                >
                  <option value="USD">USD</option>
                  <option value="EUR">EUR</option>
                  <option value="GBP">GBP</option>
                  <option value="INR">INR</option>
                </select>
              </div>
            </div>
          </section>

          {/* Benefits */}
          <section className="form-section">
            <h3>Benefits</h3>
            {renderArrayField('benefits', 'Benefits')}
          </section>

          {/* Status & Visibility */}
          <section className="form-section">
            <h3>Status & Visibility</h3>
            <div className="form-row">
              <div className="form-group">
                <label className="form-label">Status</label>
                <select
                  className="form-select"
                  value={formData.status}
                  onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                >
                  <option value="draft">Draft</option>
                  <option value="active">Active</option>
                  <option value="closed">Closed</option>
                  <option value="paused">Paused</option>
                </select>
              </div>

              <div className="form-group">
                <label className="form-label checkbox-label">
                  <input
                    type="checkbox"
                    checked={formData.isPublic}
                    onChange={(e) => setFormData({ ...formData, isPublic: e.target.checked })}
                  />
                  <span>Make this job public</span>
                </label>
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label className="form-label">Application Deadline</label>
                <input
                  type="date"
                  className="form-input"
                  value={formData.applicationDeadline}
                  onChange={(e) => setFormData({ ...formData, applicationDeadline: e.target.value })}
                />
                <small className="form-hint">Last date candidates can apply</small>
              </div>

              <div className="form-group">
                <label className="form-label">Posting Expiry Date</label>
                <input
                  type="date"
                  className="form-input"
                  value={formData.expiresAt}
                  onChange={(e) => setFormData({ ...formData, expiresAt: e.target.value })}
                />
                <small className="form-hint">Job will auto-close after this date</small>
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label className="form-label checkbox-label">
                  <input
                    type="checkbox"
                    checked={formData.autoCloseOnExpiry}
                    onChange={(e) => setFormData({ ...formData, autoCloseOnExpiry: e.target.checked })}
                  />
                  <span>Auto-close when expired</span>
                </label>
              </div>
            </div>
          </section>

          {/* Actions */}
          <div className="form-actions">
            <button
              type="button"
              className="btn-cancel"
              onClick={onClose}
              disabled={saving}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="btn-save"
              disabled={saving}
            >
              {saving ? 'Saving...' : jobId ? 'Update Job' : 'Create Job'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default JobEditor;
